import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { BACKEND_URL } from '../lib/backendUrl';
import { useAuth } from '../contexts/AuthContext';
import { ROLE_DASHBOARD_MAP } from '../constants/roles';
import { Eye, EyeOff, User, Lock } from 'lucide-react';
import Hyperspeed from './Hyperspeed';

interface InviteData {
  token: string;
  role: string;
  email: string;
}

const AcceptAdminInvite: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<InviteData | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { setAuthSession, login } = useAuth();

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setError('Invalid invite link. No token provided.');
      return;
    }

    // Parse token to get role and email (in a real app, you'd validate this on backend)
    // For now, we'll pass it to the backend
    setInvite({ token, role: '', email: '' });
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username || !password || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/invite/accept`,
        {
          token: invite?.token,
          username,
          password
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (response.data.token) {
        if (response.data.user) {
          setAuthSession(response.data.token, response.data.user);
        } else {
          const roleForLogin = invite?.role === 'ROLE_SUPER_ADMIN' ? 'super_admin' : 'club_admin';
          await login(username, password, roleForLogin);
        }

        setSuccess(true);
        const normalizedRole = (response.data?.user?.role || 'STUDENT').toUpperCase().replace('-', '_') as keyof typeof ROLE_DASHBOARD_MAP;
        const dashboardPath = ROLE_DASHBOARD_MAP[normalizedRole] || '/dashboard/student';
        setTimeout(() => {
          navigate(dashboardPath, { replace: true });
        }, 2000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to accept invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const hyperspeedOptions = {
    distortion: 'turbulentDistortion',
    speedUp: 2,
    colors: {
      roadColor: 0x080808,
      islandColor: 0x0a0a0a,
      background: 0x000000,
      shoulderLines: 0x00ffff,
      brokenLines: 0x00ffff,
      leftCars: [0xd856bf, 0x6750a2, 0xc247ac],
      rightCars: [0x03b3c3, 0x0e5ea5, 0x324555],
      sticks: 0x03b3c3
    }
  };

  if (!invite) {
    return (
      <div className="auth-page">
        <Hyperspeed effectOptions={hyperspeedOptions} />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="auth-container"
        >
          <h1 className="neon-title">NEXUS</h1>
          <p style={{ color: '#ff6b6b', textAlign: 'center' }}>
            {error || 'Loading invitation...'}
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <Hyperspeed effectOptions={hyperspeedOptions} />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="auth-container"
        style={{ maxWidth: '450px' }}
      >
        <button
          type="button"
          className="auth-back-btn"
          onClick={() => navigate('/')}
          aria-label="Go back"
        >
          ←
        </button>
        <h1 className="neon-title">NEXUS</h1>
        <p className="auth-subtitle">ACTIVATE YOUR ADMIN ACCOUNT</p>

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              backgroundColor: 'rgba(220, 53, 69, 0.2)',
              border: '1px solid #dc3545',
              color: '#ff6b6b',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px'
            }}
          >
            {error}
          </motion.div>
        )}

        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              backgroundColor: 'rgba(40, 167, 69, 0.2)',
              border: '1px solid #28a745',
              color: '#6fd66f',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px',
              textAlign: 'center'
            }}
          >
            ✓ Account created successfully! Redirecting...
          </motion.div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div style={{ position: 'relative', marginBottom: '20px' }}>
            <User size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#00ffff' }} />
            <input
              type="text"
              placeholder="Choose Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="cyber-input"
              style={{ paddingLeft: '45px' }}
              disabled={success}
              required
            />
          </div>

          <div style={{ position: 'relative', marginBottom: '20px' }}>
            <Lock size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#00ffff' }} />
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Set Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="cyber-input"
              style={{ paddingLeft: '45px', paddingRight: '45px' }}
              disabled={success}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              disabled={success}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: success ? 'not-allowed' : 'pointer',
                color: '#00ffff'
              }}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <div style={{ position: 'relative', marginBottom: '20px' }}>
            <Lock size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#00ffff' }} />
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="cyber-input"
              style={{ paddingLeft: '45px', paddingRight: '45px' }}
              disabled={success}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              disabled={success}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: success ? 'not-allowed' : 'pointer',
                color: '#00ffff'
              }}
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <motion.button
            type="submit"
            disabled={loading || success}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="cyber-button"
            style={{
              cursor: loading || success ? 'not-allowed' : 'pointer',
              opacity: loading || success ? 0.5 : 1
            }}
          >
            {loading ? 'Activating Account...' : 'Activate Account'}
          </motion.button>
        </form>
      </motion.div>
    </div>
  );
};

export default AcceptAdminInvite;
